/**
 * Shared types and fetch helper for the player column system.
 *
 * `fetchFileData(fileId, linkId?)` wraps `GET /file/{id}` and returns the
 * typed response. `PlayerColumn` owns fetching and rendering into each
 * column's DOM — this module only provides the types, the fetch helper,
 * and a `window.loadFile` shim that routes through `playerColumnManager`
 * so page-level callers (prev/next buttons, search results, comments
 * code, etc.) can switch the active column by file id.
 */

// ── Response types (mirror KollaborateServer/src/routes/file.rs) ──

export interface FileProxyInfo {
  id: number;
  width: number;
  height: number;
  url: string;
  mime_type: string;
  mime_codec: string | null;
  file_type_id: number;
  size: number;
  file_extension: string;
}

interface FileVersion {
  id: number;
  version_title: string;
  thumbnail_url: string;
}

interface FileTiming {
  fps: number;
  start_frame: number;
  drop_frame: boolean;
}

interface FileMetadata {
  width: number;
  height: number;
  language: string;
}

interface FileThumbnails {
  thumbnail_url: string;
  thumbnail_hoverscrub_url: string | null;
  poster_url: string | null;
  waveform_url: string | null;
}

interface UserInfo {
  is_file_owner: boolean;
  is_favorite: boolean;
  is_subscribed: boolean;
}

export interface WorkflowSummary {
  id: number;
  name: string;
}

export interface LinkInfo {
  is_folder_link: boolean;
  link_name: string;
  link_relative_path: string;
}

export interface GetFileResponse {
  id: number;
  department_id: number;
  department_name: string;
  path: string;
  project_id: number;
  file_type_id: number;
  file_extension: string;
  alias_to: number;
  title: string;
  original_file_url: string | null;
  mime_type: string;
  mime_codec: string | null;
  size: number;
  proxies: FileProxyInfo[];
  workflow_id: number;
  workflow_name: string;
  /** Project-level workflows. Optional — only populated by PHP's
   *  buildInitialFileData() on first column mount. The Rust /file/{id}
   *  endpoint does not currently return this field, so PlayerColumn
   *  caches the last seen value across file switches within a column. */
  workflows?: WorkflowSummary[];
  versions: FileVersion[];
  timing: FileTiming;
  metadata: FileMetadata;
  thumbnails: FileThumbnails;
  is_encoding: boolean;
  can_encode: boolean;
  user_info: UserInfo;
  link: LinkInfo;
  download_only: boolean;
}

// ── Fetch helper ──

export async function fetchFileData(fileId: number, linkId?: string): Promise<GetFileResponse> {
  let url = `/file/${fileId}`;
  if (linkId) url += `?link_id=${encodeURIComponent(linkId)}`;

  const response = await fetch(url, {});

  if (!response.ok) {
    let errorMessage = `${response.status} ${response.statusText}`;
    try {
      const body = await response.json();
      if (body?.error) errorMessage = body.error;
    } catch {
      // Response body wasn't JSON — use status text
    }
    throw new Error(errorMessage);
  }

  return response.json() as Promise<GetFileResponse>;
}

// ── window.loadFile shim ──
//
// Routes through playerColumnManager so page-level callers work correctly
// in the multi-column layout. The active column is determined by
// `playerColumns.loadFileIntoActiveColumn`.

export function loadFile(fileId: number): void {
  const w = window as any;
  if (w.playerColumns && typeof w.playerColumns.loadFileIntoActiveColumn === 'function') {
    w.playerColumns.loadFileIntoActiveColumn(fileId);
    return;
  }
  console.warn('loadFile called before playerColumns initialized');
}

if (typeof window !== 'undefined') {
  (window as any).loadFile = loadFile;
}
